import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { GoogleLogin } from '@react-oauth/google'
import { useOAuthNonce } from '../hooks/useOAuthNonce'
import { PublicClientApplication } from '@azure/msal-browser'
import { useAuth } from '../contexts/AuthContext'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { authApiService } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { useTurnstileConfig, TurnstileWidget } from '../components/Turnstile'
import { config } from '../config/environment'
import { colors, fontSize, radius, shadow, fontFamily, componentHeight, focusRing, transition } from '../styles/theme'

function loadAppleSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof AppleID !== 'undefined') { resolve(); return }
    const script = document.createElement('script')
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Apple Sign In SDK'))
    document.head.appendChild(script)
  })
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.surfaceTinted ?? '#F4FAF9',
    padding: '24px 16px',
    fontFamily: fontFamily.sans,
  } as React.CSSProperties,
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 28,
  } as React.CSSProperties,
  brandIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
  brandName: {
    fontSize: '22px',
    fontWeight: 600,
    color: colors.text,
    letterSpacing: '-0.02em',
  } as React.CSSProperties,
  card: {
    background: colors.surface,
    borderRadius: radius.lg,
    border: `1px solid ${colors.border}`,
    boxShadow: shadow.md,
    padding: '32px 32px 28px',
    width: '100%',
    maxWidth: 400,
  } as React.CSSProperties,
  title: {
    fontSize: fontSize['2xl'],
    fontWeight: 600,
    color: colors.text,
    letterSpacing: '-0.02em',
    marginBottom: 24,
  } as React.CSSProperties,
  label: {
    display: 'block',
    fontSize: fontSize.sm,
    fontWeight: 500,
    color: colors.text,
    marginBottom: 5,
    letterSpacing: '-0.01em',
  } as React.CSSProperties,
  input: {
    width: '100%',
    height: componentHeight.inputMd,
    padding: '0 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    fontSize: fontSize.sm,
    color: colors.text,
    background: colors.surface,
    boxSizing: 'border-box' as const,
    outline: 'none',
    fontFamily: fontFamily.sans,
    letterSpacing: '-0.01em',
    transition: `border-color ${transition.fast}`,
  } as React.CSSProperties,
  fieldGroup: { marginBottom: 16 } as React.CSSProperties,
  button: {
    width: '100%',
    height: componentHeight.btnLg,
    background: colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: radius.full,
    fontSize: fontSize.sm,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 8,
    fontFamily: fontFamily.sans,
    letterSpacing: '-0.01em',
    transition: `background ${transition.fast}`,
  } as React.CSSProperties,
  error: {
    background: colors.errorLight,
    color: colors.error,
    border: `1px solid ${colors.errorBorder}`,
    borderRadius: radius.md,
    padding: '10px 14px',
    fontSize: fontSize.sm,
    marginBottom: 16,
  } as React.CSSProperties,
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '20px 0',
    color: colors.textMuted,
    fontSize: fontSize.xs,
    fontFamily: fontFamily.mono,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
  dividerLine: {
    flex: 1,
    height: 1,
    background: colors.border,
  } as React.CSSProperties,
  socialBtn: {
    width: '100%',
    height: componentHeight.btnLg,
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    fontSize: fontSize.sm,
    color: colors.text,
    cursor: 'pointer',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    fontFamily: fontFamily.sans,
    letterSpacing: '-0.01em',
    transition: `background ${transition.fast}`,
  } as React.CSSProperties,
  footer: {
    textAlign: 'center' as const,
    marginTop: 20,
    fontSize: fontSize.sm,
    color: colors.textSecondary,
  } as React.CSSProperties,
}

function EyeIcon({ visible }: { visible: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      {visible
        ? <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/>
        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
      }
    </svg>
  )
}

export default function LoginPage() {
  const { login, refresh } = useAuth()
  const navigate = useNavigate()
  const socialSignInEnabled = useFeatureFlag('SocialSignIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const searchParams = new URLSearchParams(window.location.search)
  const [error, setError] = useState(searchParams.get('error') === 'microsoft' ? 'Microsoft sign-in failed' : '')
  const [loading, setLoading] = useState(false)
  const googleNonce = useOAuthNonce()
  const turnstile = useTurnstileConfig()
  const [turnstileToken, setTurnstileToken] = useState('')


  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (turnstile.enabled && !turnstileToken) { setError('Please complete the verification challenge.'); return }
    setLoading(true)
    try {
      const { mfaRequired, emailVerificationRequired } = await login(email, password, turnstileToken)
      if (emailVerificationRequired) {
        navigate('/verify-email', { state: { email: email.trim() } })
      } else if (mfaRequired) {
        navigate('/mfa')
      } else {
        navigate('/app/dashboard')
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={styles.page}>
      {/* Brand lockup */}
      <div style={styles.brand}>
        <div style={styles.brandIcon}>
          <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
            <path d="M4 3h8l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="#fff" opacity={0.9} />
            <path d="M12 3l4 4h-4V3z" fill="#fff" opacity={0.6} />
            <path d="M7 9h6M7 12h6M7 15h4" stroke="#0F9D92" strokeWidth={1.2} strokeLinecap="round" />
          </svg>
        </div>
        <span style={styles.brandName}>Doc<span style={{ color: colors.primary }}>morph</span>ia</span>
      </div>

      <div style={styles.card}>
        <div style={styles.title}>Sign in</div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email <span style={{ color: colors.error }}>*</span></label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={styles.input}
              placeholder="you@company.com"
              required
              autoFocus
              autoComplete="email"
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password <span style={{ color: colors.error }}>*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ ...styles.input, paddingRight: 40 }}
                placeholder="Your password"
                required
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, display: 'flex', alignItems: 'center', padding: 2 }}>
                <EyeIcon visible={showPw} />
              </button>
            </div>
          </div>
          {turnstile.enabled && turnstile.siteKey && (
            <TurnstileWidget siteKey={turnstile.siteKey} onToken={setTurnstileToken} />
          )}
          <button type="submit" style={styles.button} disabled={loading || (turnstile.enabled && !turnstileToken)}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = colors.primaryHover }}
            onMouseLeave={e => { e.currentTarget.style.background = colors.primary }}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <Link to="/forgot-password" style={{ fontSize: fontSize.xs, color: colors.primary, textDecoration: 'none' }}>
              Forgot password?
            </Link>
          </div>
        </form>

        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span>or</span>
          <div style={styles.dividerLine} />
        </div>

        {socialSignInEnabled ? (
          <>
            <div style={{ position: 'relative', height: componentHeight.btnLg, marginBottom: 8 }}>
              {/* Visible custom button — pointer-events disabled so clicks pass to Google button */}
              <div style={{ ...styles.socialBtn, position: 'absolute', inset: 0, marginBottom: 0, pointerEvents: 'none', zIndex: 1, borderRadius: radius.md }}>
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.548 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
                </svg>
                Sign in with Google
              </div>
              {/* Invisible Google button on top — captures clicks; no width prop so it fills the container */}
              <div style={{ position: 'absolute', inset: 0, opacity: 0, zIndex: 2 }}>
                {googleNonce.nonce && <GoogleLogin
                  key={googleNonce.nonce}
                  nonce={googleNonce.nonce}
                  onSuccess={async ({ credential }) => {
                    if (!credential) return
                    setError('')
                    setLoading(true)
                    try {
                      const signIn = await authApiService.oauthGoogle(credential)
                      if (signIn?.requiresMfa) { navigate('/mfa'); return }
                      await refresh()
                      navigate('/app/dashboard')
                    } catch (err) {
                      setError(err instanceof ApiError ? err.message : 'Google sign-in failed')
                    } finally {
                      setLoading(false)
                      googleNonce.renew()
                    }
                  }}
                  onError={() => { setError('Google sign-in failed'); googleNonce.renew() }}
                  shape="rectangular"
                  theme="outline"
                  text="signin_with"
                />}
              </div>
            </div>
            {config.appleClientId && (
              <button
                type="button"
                style={{
                  ...styles.socialBtn,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
                disabled={loading}
                onClick={async () => {
                  setError('')
                  setLoading(true)
                  try {
                    await loadAppleSdk()
                    const { nonce: appleNonce } = await authApiService.oauthNonce()
                    AppleID.auth.init({
                      clientId: config.appleClientId,
                      scope: 'name email',
                      redirectURI: window.location.origin,
                      usePopup: true,
                      nonce: appleNonce,
                    })
                    const result = await AppleID.auth.signIn()
                    const firstName = result.user?.name?.firstName ?? undefined
                    const lastName = result.user?.name?.lastName ?? undefined
                    const signIn = await authApiService.oauthApple(result.authorization.id_token, firstName, lastName)
                    if (signIn?.requiresMfa) { navigate('/mfa'); return }
                    await refresh()
                    navigate('/app/dashboard')
                  } catch (err: unknown) {
                    if ((err as { error?: string })?.error !== 'popup_closed_by_user') {
                      setError(err instanceof ApiError ? err.message : 'Apple sign-in failed')
                    }
                  } finally {
                    setLoading(false)
                  }
                }}
              >
                <svg width="12" height="16" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" fill="#000">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                Sign in with Apple
              </button>
            )}
            {config.microsoftClientId && (
              <button
                type="button"
                style={{
                  ...styles.socialBtn,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
                disabled={loading}
                onClick={async () => {
                  if (!config.microsoftClientId) return
                  setError('')
                  setLoading(true)
                  try {
                    console.log('[MSAL] creating fresh PCA for loginRedirect')
                    const msal = new PublicClientApplication({
                      auth: {
                        clientId: config.microsoftClientId,
                        redirectUri: window.location.origin + '/auth-redirect',
                      },
                    })
                    await msal.initialize()
                    console.log('[MSAL] calling loginRedirect')
                    const { nonce: msNonce } = await authApiService.oauthNonce()
                    await msal.loginRedirect({ scopes: ['openid', 'profile', 'email'], nonce: msNonce })
                  } catch (err: unknown) {
                    console.error('[MSAL] loginRedirect error:', err)
                    setError(err instanceof ApiError ? err.message : 'Microsoft sign-in failed')
                    setLoading(false)
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#00a4ef"/>
                  <rect x="1" y="11" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </button>
            )}
          </>
        ) : (
          <>
            <button style={styles.socialBtn} title="Coming soon" disabled>
              Continue with Google
            </button>
            <button style={styles.socialBtn} title="Coming soon" disabled>
              Continue with Microsoft
            </button>
            <button style={styles.socialBtn} title="Coming soon" disabled>
              Continue with Apple
            </button>
          </>
        )}

        <div style={styles.footer}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 500 }}>Sign up</Link>
        </div>
      </div>

      {/* Footer links */}
      <div style={{ marginTop: 20, display: 'flex', gap: 16, fontSize: fontSize.xs, color: colors.textMuted }}>
        <Link to="/demo" style={{ color: colors.textMuted, textDecoration: 'none' }}>Demo</Link>
        <span>·</span>
        <a href="#" style={{ color: colors.textMuted, textDecoration: 'none' }}>Terms</a>
        <span>·</span>
        <a href="#" style={{ color: colors.textMuted, textDecoration: 'none' }}>Privacy</a>
      </div>
    </div>
  )
}
